import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { Box, Button, Col } from '@landsbankinn/ui-kit';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ButtonContainer } from 'src/components/Layout';
import { AppHubStateType, type AppHubState } from 'src/services/AppHubService';
import { internal } from 'src/services/ConsoleLoggerService';
import { ServicesProvider } from 'src/services/ServicesProvider';
import {
  WindowLocationServiceToken,
  type IWindowLocationService,
} from 'src/services/WindowLocationService';
import { useApplicationClientContext } from 'src/state/client';
import { useAppService } from './AppContext';
import { res } from './AppQR.res';

const HEIGHT = 91;
const WIDTH = 58;

const LARGE_HEIGHT = 260;
const LARGE_WIDTH = 160;

// Used for QR code section, handles responsiveness and animation
const SectionWrapper = styled(Box)<{ isMobile: boolean }>`
  & {
    ${(props) => (props.isMobile ? '' : 'margin: 28px auto 0')}
  }
  /* TODO: Make sure next line can be removed*/
  // height: ${(props) => (props.isMobile ? '100px' : '200px')};
  width: ${(props) => (props.isMobile ? 'inherit' : '200px')};
  position: relative;
  @media (min-width: 1320px) {
    background: linear-gradient(315deg, #2f6183 0%, #15324c 100%);
    height: 580px;
    width: 580px;
    padding: 90px;
    margin: 0;
  }

  opacity: 0;
  animation: fadeInAnimation ease-in 0.7s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
  @keyframes fadeInAnimation {
    0% {
      opacity: 0;
    }
    25% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const Img = styled.img<{ $fading: boolean; $restart: boolean }>`
  restart: ${(props) => props.$restart.toString()}
  fading: ${(props) => props.$fading.toString()}
  height: 100%;
  width: 100%;
  opacity: ${(props) => (props.$fading ? 0 : 1)};
  transition: all 1s ease-in-out;
  transition-delay: ${(props) => (props.$fading ? 0 : 0.3)}s;
`;

const Phone = styled.svg<{ $loading: boolean; errorShake: boolean }>`
  position: absolute;
  loading: ${(props) => props.$loading.toString()};
  width: ${(props) => (props.$loading ? LARGE_WIDTH : WIDTH)}px;
  height: ${(props) => (props.$loading ? LARGE_HEIGHT : HEIGHT)}px;
  top: calc(
    50% - (${(props) => (props.$loading ? LARGE_HEIGHT : HEIGHT)}px / 2)
  );
  right: calc(
    50% - (${(props) => (props.$loading ? LARGE_WIDTH : WIDTH)}px / 2)
  );
  > path {
    stroke-width: ${(props) => (props.$loading ? 0 : 4)};
  }

  @media (max-width: 1319px) {
    top: calc(50% - (${(props) => (props.$loading ? 130 : 43)}px / 2));
    right: calc(50% - (${(props) => (props.$loading ? 80 : 27)}px / 2));
    height: calc(${(props) => (props.$loading ? 130 : 43)}px);
    width: calc(${(props) => (props.$loading ? 80 : 27)}px);
    > path {
      stroke-width: 2;
    }
  }
  animation: ${(props) => props.errorShake && 'shake 0.5s'};
  animation-iteration-count: 1;

  transition:
    height 0.3s,
    width 0.3s,
    top 0.3s,
    right 0.3s,
    stroke-width 2s;
  transition-timing-function: ease-in-out;
  transition-delay: ${(props) => (props.$loading ? 0.8 : 0)}s;

  @keyframes shake {
    0% {
      transform: translate(0, 0);
    }
    5% {
      transform: translate(0, 0);
    }
    15% {
      transform: translate(-20px, 0);
    }
    30% {
      transform: translate(20px, 0);
    }
    45% {
      transform: translate(-10px, 0);
    }
    60% {
      transform: translate(10px, 0);
    }
    75% {
      transform: translate(-5px, 0);
    }
    90% {
      transform: translate(5px, 0);
    }
    100% {
      transform: translate(0, 0);
    }
  }
`;

function isStateEqual(prev: AppHubState, current: AppHubState) {
  return JSON.stringify(prev) === JSON.stringify(current);
}

type AppQRStep =
  | { type: 'empty' }
  | { type: 'login'; url: string; image: string }
  | { type: 'pre-pending'; image: string }
  | { type: 'pending' }
  | { type: 'completed' }
  | { type: 'error' }
  | { type: 'suspended' };

export function AppQR(props: { isMobile?: boolean }) {
  const previousState = useRef<AppHubState | undefined>(undefined);

  const { state, showQrCode } = useAppService();

  const [currentStep, setCurrentStep] = useState<AppQRStep>({ type: 'empty' });

  useEffect(() => {
    if (
      previousState.current === undefined ||
      (state && !isStateEqual(previousState.current, state))
    ) {
      if (state) {
        switch (state.type) {
          case AppHubStateType.Login:
            setCurrentStep({
              type: 'login',
              url: state.qr?.url || '',
              image: state.qr?.image || '',
            });
            break;
          case AppHubStateType.Pending:
            if (
              previousState.current?.type === AppHubStateType.Login &&
              previousState.current.qr
            ) {
              setCurrentStep({
                type: 'pre-pending',
                image: previousState.current.qr.image,
              });
            }
            setTimeout(
              () => {
                setCurrentStep({ type: 'pending' });
              },
              previousState.current === undefined ? 0 : 1200,
            );
            break;
          case AppHubStateType.Completed:
            setCurrentStep({ type: 'completed' });
            break;
          case AppHubStateType.Error:
            setCurrentStep({ type: 'error' });
            break;
          case AppHubStateType.Suspended:
            setCurrentStep({ type: 'suspended' });
            break;
          default:
            setCurrentStep({ type: 'empty' });
            break;
        }
      }

      previousState.current = state;
    }
  }, [state, previousState]);

  const loading =
    currentStep.type === 'pre-pending' ||
    currentStep.type === 'pending' ||
    currentStep.type === 'error' ||
    currentStep.type === 'suspended';
  const [clientState] = useApplicationClientContext();

  const handleOnQrClick = () => {
    // Only when in debug mode
    if (
      state?.type === AppHubStateType.Login &&
      state.qr !== undefined &&
      state.qr.debugUrl &&
      state.qr.url
    ) {
      navigator.clipboard.writeText(state.qr.url);
      internal.info(`Copied authentication url '${state.qr.url}' to clipboard`);
    }
  };

  return (
    <AppQRView
      currentStep={currentStep}
      errors={clientState.errors}
      isMobile={props.isMobile}
      loading={loading}
      onQRClick={handleOnQrClick}
      showQrCode={showQrCode}
    />
  );
}

type AppQRViewProps = {
  currentStep: AppQRStep;
  errors?: string[];
  isMobile?: boolean;
  loading: boolean;
  onQRClick: () => void;
  showQrCode: boolean;
  state?: AppHubState;
};

function AppQRView({
  currentStep,
  errors,
  isMobile,
  loading,
  onQRClick,
  showQrCode,
}: AppQRViewProps) {
  const { r } = useResource();
  const navigate = useNavigate();
  const windowLocationService =
    ServicesProvider.getSingleton<IWindowLocationService>(
      WindowLocationServiceToken,
      navigate,
    );

  return (
    <Col>
      <SectionWrapper isMobile={isMobile || false}>
        <Box>
          {(currentStep.type === 'login' ||
            currentStep.type === 'pre-pending') && (
            <>
              {currentStep.type === 'login' && isMobile && (
                <ButtonContainer>
                  <Button
                    instrumentAs="login_with_app"
                    variant="primary"
                    width="100%"
                    rel="noreferrer"
                    onClick={() => {
                      windowLocationService.goto(currentStep.url, navigate);
                    }}
                  >
                    {r(res.loginWithApp)}
                  </Button>
                </ButtonContainer>
              )}
              {!isMobile && showQrCode === true && (
                <>
                  <Img
                    src={`data:image/png;base64, ${currentStep.image}`}
                    alt="QR Code"
                    $fading={currentStep.type === 'pre-pending'}
                    $restart={errors !== undefined}
                    onClick={onQRClick}
                  />
                </>
              )}
            </>
          )}
          {!isMobile && (
            <Phone
              width={loading ? LARGE_WIDTH : WIDTH}
              height={loading ? LARGE_HEIGHT : HEIGHT}
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              $loading={loading}
              errorShake={
                currentStep.type === 'error' || currentStep.type === 'suspended'
              }
            >
              <path
                d="M47.188 2H10.142C5.66389 2 2 5.67902 2 10.1756V80.4857C2 84.9822 5.66389 88.6613 10.142 88.6613H47.188C51.6661 88.6613 55.33 84.9822 55.33 80.4857V10.1756C55.33 5.67902 51.6661 2 47.188 2Z"
                fill="white"
                stroke="#163E63"
                strokeMiterlimit="10"
              />
              <rect
                opacity="0.2"
                x="21.9988"
                y="8.66626"
                width="13.3325"
                height="3.66644"
                rx="1.83322"
                fill="#163E63"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M37.1308 55.5965C39.1307 55.5965 39.5306 54.3966 39.1307 53.1967C38.7307 51.9968 35.1309 41.5974 35.1309 41.5974C35.1309 41.5974 31.5311 52.3967 31.1312 53.9966C30.3312 56.3965 27.1314 55.5965 27.9314 52.7967C28.7313 50.3969 33.131 36.3977 33.531 34.7978C34.331 32.398 35.1309 31.598 37.5308 31.1981C35.9309 31.1981 28.7313 31.1981 26.7314 31.1981C24.7316 31.1981 23.5316 32.398 23.1317 33.9979C22.7317 35.5978 17.932 50.7968 17.532 52.3967C17.132 53.9966 15.9321 55.1966 14.7322 55.5965C16.3321 55.5965 37.1308 55.5965 37.1308 55.5965Z"
                fill="#163E63"
              />
            </Phone>
          )}
        </Box>
      </SectionWrapper>
    </Col>
  );
}
